import React from 'react';
import { FormField } from '../../../components/forms/FormField/FormField';
import { Button } from '../../../components/common/Button/Button';
import { useCertificateForm } from '../hooks/useCertificateForm';
import { CertificateFormProps } from '../../../types/certificate';
import styles from './CertificateForm.module.css';

/**
 * 产品合格证信息录入表单组件
 * 根据合格证生成规范实现信息录入功能
 * 具备完整的表单功能、数据验证、响应式设计、独立可测试和易于集成的特点
 */
export const CertificateForm: React.FC<CertificateFormProps> = ({
  onSubmit,
  initialData,
  disabled = false,
  className = ''
}) => {
  const {
    formState,
    handleInputChange,
    handleSubmit,
    resetForm
  } = useCertificateForm({
    onSubmit,
    initialData
  });

  const { data, errors, isSubmitting, isValid } = formState;

  return (
    <div className={`${styles.certificateForm} ${className}`}>
      <div className={styles.formHeader}>
        <h2 className={styles.formTitle}>产品合格证信息录入</h2>
        <p className={styles.formDescription}>
          请填写以下信息以生成产品合格证，所有标有 * 的字段为必填项
        </p>
      </div>

      <form onSubmit={handleSubmit} className={styles.formContainer} noValidate>
        <div className={styles.formGrid}>
          <FormField
            label="订单号"
            name="orderNumber"
            type="text"
            value={data.orderNumber}
            onChange={(value) => handleInputChange('orderNumber', value)}
            error={errors.orderNumber}
            required
            disabled={disabled || isSubmitting}
            placeholder="请输入订单号"
            className={styles.formFieldFull}
          />

          <FormField
            label="批号"
            name="batchNumber"
            type="text"
            value={data.batchNumber}
            onChange={(value) => handleInputChange('batchNumber', value)}
            error={errors.batchNumber}
            required
            disabled={disabled || isSubmitting}
            placeholder="请输入批号"
          />

          <FormField
            label="桶数"
            name="bucketCount"
            type="number"
            value={data.bucketCount}
            onChange={(value) => handleInputChange('bucketCount', value)}
            error={errors.bucketCount}
            required
            disabled={disabled || isSubmitting}
            placeholder="请输入桶数"
          />

          <FormField
            label="生产日期"
            name="productionDate"
            type="date"
            value={data.productionDate}
            onChange={(value) => handleInputChange('productionDate', value)}
            error={errors.productionDate}
            required
            disabled={disabled || isSubmitting}
          />

          <FormField
            label="发货日期"
            name="shipmentDate"
            type="date"
            value={data.shipmentDate}
            onChange={(value) => handleInputChange('shipmentDate', value)}
            error={errors.shipmentDate}
            required
            disabled={disabled || isSubmitting}
          />

          <FormField
            label="检测报告生成日期"
            name="testReportDate"
            type="date"
            value={data.testReportDate}
            onChange={(value) => handleInputChange('testReportDate', value)}
            error={errors.testReportDate}
            required
            disabled={disabled || isSubmitting}
          />
        </div>

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="secondary"
            onClick={resetForm}
            disabled={disabled || isSubmitting}
            className={styles.resetButton}
          >
            重置表单
          </Button>
          
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
            disabled={disabled || !isValid}
            className={styles.submitButton}
          >
            {isSubmitting ? '生成中...' : '生成合格证'}
          </Button>
        </div>
      </form>
    </div>
  );
};